import { useEffect, useRef, useState } from 'react';
import { useImages } from '../context/ImageContext';

// The longest message the server takes
const ChatLength = 500;

// The game's icons, written in a message as :name: (like emoji), by name: the picture (see ImageContext) and what it is
const ChatIcons = {
    yellow_cube: ['YellowCube', 'Yellow token'],
    blue_cube: ['BlueCube', 'Blue token'],
    food: ['Food', 'Food'],
    resource: ['Resource', 'Resource'],
    science: ['Science', 'Science'],
    culture: ['Culture', 'Culture'],
    happy: ['Happiness', 'Happy face'],
    strength: ['Strength', 'Strength'],
    colonization: ['ExplorationResource', 'Colonization'],
    civil_action: ['CivilAction', 'Civil action'],
    military_action: ['MilitaryAction', 'Military action'],
    farm: ['Farm', 'Farm'],
    mine: ['Mine', 'Mine'],
    lab: ['Lab', 'Lab'],
    temple: ['Temple', 'Temple'],
    library: ['Library', 'Library'],
    theater: ['Theater', 'Theater'],
    arena: ['Arena', 'Arena'],
    infantry: ['Infantry', 'Infantry'],
    cavalry: ['Cavalry', 'Cavalry'],
    artillery: ['Artillery', 'Artillery'],
    air_forces: ['AirForces', 'Air forces'],
    government: ['Civil', 'Government'],
    wonder: ['Construction', 'Wonder'],
    exploration: ['Exploration', 'Exploration'],
};
const IconCode = /:([a-z_]+):/g;
// The icon name being typed at the end of the text before the cursor: a ":" (at the start or after a space) and at least
// one letter of the name
const Typing = /(^|\s):([a-z_]+)$/;
// The most suggestions shown at once
const MostSuggestions = 8;

// The icons whose names fit what is typed: those starting with it first, then those containing it
const Suggestions = (typed) => {
    const names = Object.keys(ChatIcons);
    return [...names.filter(name => name.startsWith(typed)), ...names.filter(name => !name.startsWith(typed) && name.includes(typed))]
        .slice(0, MostSuggestions);
};

// One of the icons, with its name on hover
function ChatIcon({name})
{
    const { images } = useImages();
    const [image, label] = ChatIcons[name];
    return <img className="tta-chat-icon" src={images[image]?.src} alt={`:${name}:`} title={label}/>;
}

// A message's text, its :name: icon codes drawn as the icons (any other :word: left as written)
function ChatText({text})
{
    const parts = [];
    let last = 0;
    for (const match of text.matchAll(IconCode))
    {
        if (ChatIcons[match[1]])
        {
            parts.push(text.slice(last, match.index), <ChatIcon key={match.index} name={match[1]}/>);
            last = match.index + match[0].length;
        }
    }
    parts.push(text.slice(last));
    return <>{parts}</>;
}

// When a message was written (the server's UTC "YYYY-MM-DD HH:MM:SS"), in the reader's time: "14:05" today, else
// "Oct 21, 14:05" (the journal's times too)
export const When = (at) => {
    const date = new Date(`${at.replace(' ', 'T')}Z`);
    const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return date.toDateString() === new Date().toDateString() ? time
        : `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${time}`;
};

// The game's chat, for its players: the messages, oldest first (the newest in view), each with who wrote it and when,
// and a box to write one (Enter sends it). The game's icons go in as :name:, with the names that fit offered while one is
// typed (arrows to choose, Enter or Tab to take it, Esc to close). Messages: [{ id, seat, text, at }]; Players: the
// players' names, by seat; OnSend(text): a promise of { ok, error? }
function Chat({Messages, Players, PlayerColors, OnSend})
{
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    // The icon name being typed (null: none), the suggestion picked, and whether the suggestions were closed (Esc)
    const [typed, setTyped] = useState(null);
    const [picked, setPicked] = useState(0);
    const [closed, setClosed] = useState(false);
    const list = useRef(null);
    const input = useRef(null);
    // (the newest message in view as they come)
    useEffect(() => {
        if (list.current)
        {
            list.current.scrollTop = list.current.scrollHeight;
        }
    }, [Messages.length]);

    const suggestions = typed !== null && !closed ? Suggestions(typed) : [];

    // What is typed before the cursor decides whether an icon name is being written
    const edited = (value, cursor) => {
        setDraft(value);
        const name = Typing.exec(value.slice(0, cursor))?.[2] ?? null;
        if (name !== typed)
        {
            setPicked(0);
            setClosed(false);
        }
        setTyped(name);
    };

    // The icon name being typed replaced by the whole code, with a space after it
    const take = (name) => {
        const element = input.current;
        const cursor = element.selectionStart;
        const before = draft.slice(0, cursor).replace(/:[a-z_]+$/, `:${name}: `);
        const value = (before + draft.slice(cursor)).slice(0, ChatLength);
        setDraft(value);
        setTyped(null);
        // (the cursor after the code)
        requestAnimationFrame(() => { element.focus(); element.setSelectionRange(before.length, before.length); });
    };

    const send = async () => {
        const text = draft.trim();
        if (!text || sending)
        {
            return;
        }
        setSending(true);
        const result = await OnSend(text).catch(e => ({ ok: false, error: e.message }));
        setSending(false);
        if (result.ok)
        {
            setDraft('');
            setTyped(null);
            setError(null);
        } else {
            setError(result.error ?? 'The message could not be sent');
        }
    };

    const keyDown = (e) => {
        if (suggestions.length > 0)
        {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp')
            {
                e.preventDefault();
                const step = e.key === 'ArrowDown' ? 1 : -1;
                setPicked((picked + step + suggestions.length) % suggestions.length);
                return;
            }
            if (e.key === 'Enter' || e.key === 'Tab')
            {
                e.preventDefault();
                take(suggestions[Math.min(picked, suggestions.length - 1)]);
                return;
            }
            if (e.key === 'Escape')
            {
                setClosed(true);
                return;
            }
        }
        if (e.key === 'Enter')
        {
            send();
        }
    };

    return (
        <div className="tta tta-left tta-chat">
            <div className="tta-section-title">Chat</div>
            <div className="tta-chat-messages" ref={list}>
                {Messages.length === 0 ? <div className="tta-muted">No messages yet</div> :
                    Messages.map(message =>
                        <div key={message.id} className="tta-chat-message">
                            <span className="tta-chat-name" style={{ color: PlayerColors[message.seat] }}>{Players[message.seat]}</span>
                            <span className="tta-chat-time">{When(message.at)}</span>
                            <div className="tta-chat-text"><ChatText text={message.text}/></div>
                        </div>)}
            </div>
            <div className="tta-chat-write">
                <div className="tta-chat-input">
                    {suggestions.length > 0 &&
                        <div className="tta-chat-suggestions">
                            {suggestions.map((name, index) =>
                                <div key={name} className={`tta-chat-suggestion${index === picked ? ' picked' : ''}`}
                                    onMouseEnter={() => setPicked(index)}
                                    onMouseDown={(e) => { e.preventDefault(); take(name); }}>
                                    <ChatIcon name={name}/> :{name}:
                                </div>)}
                        </div>}
                    <input ref={input} type="text" value={draft} maxLength={ChatLength} placeholder="Write a message (game icons: type : and a name, e.g. :food:)"
                        onChange={(e) => edited(e.target.value, e.target.selectionStart)} onKeyDown={keyDown}
                        onBlur={() => setTyped(null)}/>
                </div>
                <button className="tta-button dark" disabled={!draft.trim() || sending} onClick={send}>Send</button>
            </div>
            {error && <div style={{ color: '#b13a2a', fontWeight: 'bold' }}>{error}</div>}
        </div>
    );
}

export default Chat;
